import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Plus, Trash, PencilSimple, UserCircle, Key, LockOpen } from '@phosphor-icons/react';

const MACHT_LABELS = [
  { key: 'artikelen_bekijken', label: 'Artikelen bekijken', hint: 'Kan artikelenlijst + detail inzien', icon: '📄' },
  { key: 'artikelen_aanmaken', label: 'Artikelen aanmaken', hint: 'Kan nieuwe artikels toevoegen', icon: '➕' },
  { key: 'artikelen_bewerken', label: 'Artikelen bewerken', hint: 'Kan bestaande artikels wijzigen', icon: '✏️' },
  { key: 'artikelen_verwijderen', label: 'Artikelen verwijderen', hint: 'Kan artikels in de prullenbak plaatsen', icon: '🗑️' },
  { key: 'prijs_wijzigen', label: 'Prijzen aanpassen', hint: 'Mag verkoopprijzen manueel wijzigen (bulk-wizard + per lijn)', icon: '💰' },
  { key: 'bestelbonnen', label: 'Bestellingen bekijken', hint: 'Kan alle bestelbonnen inzien', icon: '📦' },
  { key: 'bestellingen_aanmaken', label: 'Bestellingen aanmaken', hint: 'Kan nieuwe bestelbonnen opmaken (verkoop onderweg)', icon: '🛒' },
  // ─── NIEUW (2026-07): per-document machtigingen ipv één 'bestellingen_aanmaken' die alles ontsloot ───
  { key: 'facturen_toegang', label: 'Facturen', hint: 'Toegang tot /facturen en /proforma (bekijken + maken)', icon: '🧾' },
  { key: 'leverbonnen_toegang', label: 'Leverbonnen + Snel-scan', hint: 'Toegang tot /leveringsbonnen en /snel-scan (bekijken + maken)', icon: '🚚' },
  { key: 'offertes_toegang', label: 'Offertes', hint: 'Toegang tot /offertes (bekijken + maken)', icon: '📑' },
  { key: 'creditnotas_toegang', label: 'Creditnota\'s', hint: 'Toegang tot /creditnotas (bekijken + maken)', icon: '↩️' },
  { key: 'herhaald_toegang', label: 'Herhaalde documenten', hint: 'Toegang tot /herhaald (recurrente facturen)', icon: '🔁' },
  // ─── /nieuw ───
  { key: 'rapporten_bekijken', label: 'Rapporten bekijken', hint: 'Dashboard-grafieken, BTW-rapporten, commissies', icon: '📊' },
  { key: 'verkooprapport_bekijken', label: 'Verkooprapport bekijken', hint: 'Gedetailleerd verkooprapport per factuur (winst, marge, commissie, Bestelbon → Leverbon → Factuur)', icon: '📈' },
  { key: 'commissies_beheren', label: 'Commissies beheren', hint: 'Toegang tot Commissies-pagina, Commissie-matrix en commissie-regels bewerken per verkoper', icon: '💶' },
  { key: 'commissies_eigen_zien', label: 'Eigen commissies zien', hint: 'Verkoper mag zijn EIGEN commissie-overzicht bekijken (enkel eigen periode, zonder anderen)', icon: '💰' },
  { key: 'exporteren', label: 'Exporteren (Excel/CSV/CODA)', hint: 'Boekhoud-export, CODA-download, artikels CSV', icon: '📤' },
  { key: 'klanten_aanmaken', label: 'Klanten aanmaken/bewerken', hint: 'Anders: enkel selecteren uit bestaande klantenlijst', icon: '👥' },
  { key: 'kortingen_geven', label: 'Kortingen toekennen', hint: 'Activeert korting-veld op bestelbon-lijnen', icon: '🏷️' },
  // Kassa-specifieke machtigingen
  { key: 'kassa_gebruiken', label: 'Kassa gebruiken', hint: 'Toegang tot /kassa pagina + tickets afrekenen', icon: '🛒', groep: 'kassa' },
  { key: 'kassa_retour', label: 'Kassa-retour boeken', hint: 'Mag retour-modus activeren en terugbetalingsticket boeken', icon: '↩️', groep: 'kassa' },
  { key: 'kassa_korting', label: 'Korting op kassa-ticket', hint: 'Mag prijzen handmatig aanpassen op een kassa-lijn', icon: '🏷️', groep: 'kassa' },
  { key: 'kassa_z_rapport', label: 'Z-rapport bekijken', hint: 'Toegang tot dag-overzicht (Z-rapport) en maandrapport van de kassa', icon: '📊', groep: 'kassa' },
  { key: 'kassa_ticket_openen', label: 'Ticket-details openen', hint: 'Mag ticket-detail-modal openen vanuit Z-rapport (artikels + verkoper + klant zien)', icon: '🧾', groep: 'kassa' },
  { key: 'kassa_ticket_verwijderen', label: 'Ticket/regel verwijderen (admin)', hint: 'Mag volledige tickets of individuele regels verwijderen — gevoelige actie', icon: '🗑️', groep: 'kassa' },
  // Beheer
  { key: 'gebruikers_beheren', label: 'Gebruikers beheren', hint: 'Verkopers aanmaken, PIN-codes resetten, rollen toekennen', icon: '👤' },
  { key: 'instellingen_wijzigen', label: 'Instellingen wijzigen', hint: 'Bedrijfsgegevens, BTW, PDF-layout, automatiseringen', icon: '⚙️' },
  { key: 'machtigingen_toewijzen', label: 'Machtigingen toewijzen', hint: 'Kan zelf de rechten van andere verkopers wijzigen', icon: '🔐' },
];

// Rollen-presets — matrix-stijl volgens de aangeleverde machtigingstabel
const ROL_PRESETS = {
  'admin': {
    label: 'Beheerder', kleur: '#2563EB',
    macht: {
      artikelen_bekijken: true, artikelen_aanmaken: true, artikelen_bewerken: true, artikelen_verwijderen: true,
      prijs_wijzigen: true, bestelbonnen: true, bestellingen_aanmaken: true, rapporten_bekijken: true,
      facturen_toegang: true, leverbonnen_toegang: true, offertes_toegang: true, creditnotas_toegang: true, herhaald_toegang: true,
      verkooprapport_bekijken: true, commissies_beheren: true, commissies_eigen_zien: true,
      exporteren: true, klanten_aanmaken: true, kortingen_geven: true,
      kassa_gebruiken: true, kassa_retour: true, kassa_korting: true, kassa_z_rapport: true, kassa_ticket_openen: true, kassa_ticket_verwijderen: true,
      gebruikers_beheren: true, instellingen_wijzigen: true, machtigingen_toewijzen: true,
    },
  },
  'manager': {
    // Label naar gebruikers-vraag: Verkoper i.p.v. Manager
    label: 'Verkoper', kleur: '#059669',
    macht: {
      artikelen_bekijken: true, artikelen_aanmaken: false, artikelen_bewerken: false, artikelen_verwijderen: false,
      prijs_wijzigen: false, bestelbonnen: true, bestellingen_aanmaken: true, rapporten_bekijken: false,
      facturen_toegang: false, leverbonnen_toegang: false, offertes_toegang: false, creditnotas_toegang: false, herhaald_toegang: false,
      verkooprapport_bekijken: false, commissies_beheren: false, commissies_eigen_zien: true,
      exporteren: false, klanten_aanmaken: true, kortingen_geven: true,
      kassa_gebruiken: false, kassa_retour: false, kassa_korting: false, kassa_z_rapport: false, kassa_ticket_openen: false, kassa_ticket_verwijderen: false,
      gebruikers_beheren: false, instellingen_wijzigen: false, machtigingen_toewijzen: false,
    },
  },
  'kassa_verkoper': {
    label: 'Kassa-verkoper', kleur: '#FF6B0D',
    macht: {
      artikelen_bekijken: true, artikelen_aanmaken: false, artikelen_bewerken: false, artikelen_verwijderen: false,
      prijs_wijzigen: false, bestelbonnen: false, bestellingen_aanmaken: false, rapporten_bekijken: false,
      facturen_toegang: false, leverbonnen_toegang: false, offertes_toegang: false, creditnotas_toegang: false, herhaald_toegang: false,
      verkooprapport_bekijken: false, commissies_beheren: false, commissies_eigen_zien: false,
      exporteren: false, klanten_aanmaken: true, kortingen_geven: false,
      kassa_gebruiken: true, kassa_retour: false, kassa_korting: false, kassa_z_rapport: false, kassa_ticket_openen: true, kassa_ticket_verwijderen: false,
      gebruikers_beheren: false, instellingen_wijzigen: false, machtigingen_toewijzen: false,
    },
  },
  'medewerker': {
    label: 'Medewerker', kleur: '#D97706',
    macht: {
      artikelen_bekijken: true, artikelen_aanmaken: true, artikelen_bewerken: false, artikelen_verwijderen: false,
      prijs_wijzigen: false, bestelbonnen: true, bestellingen_aanmaken: true, rapporten_bekijken: false,
      facturen_toegang: true, leverbonnen_toegang: true, offertes_toegang: false, creditnotas_toegang: false, herhaald_toegang: false,
      verkooprapport_bekijken: false, commissies_beheren: false, commissies_eigen_zien: false,
      exporteren: false, klanten_aanmaken: true, kortingen_geven: true,
      kassa_gebruiken: true, kassa_retour: false, kassa_korting: false, kassa_z_rapport: false, kassa_ticket_openen: false, kassa_ticket_verwijderen: false,
      gebruikers_beheren: false, instellingen_wijzigen: false, machtigingen_toewijzen: false,
    },
  },
  'gast': {
    label: 'Gast', kleur: '#6B7280',
    macht: {
      artikelen_bekijken: true, artikelen_aanmaken: false, artikelen_bewerken: false, artikelen_verwijderen: false,
      prijs_wijzigen: false, bestelbonnen: false, bestellingen_aanmaken: false, rapporten_bekijken: false,
      facturen_toegang: false, leverbonnen_toegang: false, offertes_toegang: false, creditnotas_toegang: false, herhaald_toegang: false,
      verkooprapport_bekijken: false, commissies_beheren: false, commissies_eigen_zien: false,
      exporteren: false, klanten_aanmaken: false, kortingen_geven: false,
      kassa_gebruiken: false, kassa_retour: false, kassa_korting: false, kassa_z_rapport: false, kassa_ticket_openen: false, kassa_ticket_verwijderen: false,
      gebruikers_beheren: false, instellingen_wijzigen: false, machtigingen_toewijzen: false,
    },
  },
};

// Apple-stijl iOS-toggle (Apple Watch Ultra-oranje zoals in Watch-instellingen)
function IOSSwitch({ checked, onChange, testid }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={(e) => { e.stopPropagation(); onChange(!checked); }}
      className="relative inline-flex shrink-0 items-center transition-all duration-200"
      style={{
        width: 52, height: 32, borderRadius: 999,
        backgroundColor: checked ? '#FF6B0D' : '#E5E7EB',
        boxShadow: checked
          ? '0 0 0 1px rgba(255,107,13,0.25), 0 0 12px rgba(255,107,13,0.45), inset 0 1px 2px rgba(0,0,0,0.08)'
          : 'inset 0 0 0 1px rgba(0,0,0,0.05)',
        cursor: 'pointer', border: 0, padding: 0,
      }}
      data-testid={testid}
    >
      <span
        style={{
          position: 'absolute',
          left: checked ? 23 : 3,
          top: 3,
          width: 26, height: 26, borderRadius: '50%',
          background: '#FFFFFF',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25), 0 1px 2px rgba(0,0,0,0.15)',
          transition: 'left 0.18s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      />
    </button>
  );
}

const emptyForm = {
  naam: '',
  email: '',
  tel: '',
  pincode: '',
  password: '',
  actief: true,
  rol: '', // '' | 'admin' | 'manager' | 'medewerker' | 'gast' | 'custom'
  // BUG-FIX: initialiseer ALLE 22 machtigingen — anders worden ontbrekende keys door
  // de backend gereset naar `false` bij elke save (`m.get(k, False)`).
  // `bestelbonnen` blijft default `true` zodat een nieuwe verkoper standaard zijn eigen
  // bestelbonnen kan inzien (zoals de oude defaults).
  machtigingen: MACHT_LABELS.reduce((acc, m) => {
    acc[m.key] = m.key === 'bestelbonnen';
    return acc;
  }, {}),
  commissie_config: { type: 'uit', basis: 'excl_btw', vast_pct: 0, getrapt: [] },
  commissie_regels: [],
};

/**
 * Bouw een volledig machtigingen-object met ALLE keys uit MACHT_LABELS,
 * plus eventueel "verborgen" keys uit de bestaande verkoper-data (legacy / niet getoond in UI).
 * Zonder dit zou de form keys missen en de backend ze resetten naar false
 * bij elke save (bug: machtigingen niet wijzigbaar).
 */
function _alleMachtigingen(override = {}) {
  const result = {};
  // 1. Alle gekende machtigingen uit MACHT_LABELS
  for (const m of MACHT_LABELS) {
    result[m.key] = !!override[m.key];
  }
  // 2. Bewaar legacy/verborgen keys die niet in MACHT_LABELS zitten
  //    (bv. `klant_groep_beperkt`) zodat ze niet stilletjes worden gedropt op save.
  for (const k of Object.keys(override || {})) {
    if (!(k in result)) result[k] = !!override[k];
  }
  return result;
}

// Helper: probeer een rol af te leiden uit de combinatie machtigingen (voor oudere records zonder rol-veld)
function detecteerRolUitMachtigingen(m) {
  if (!m) return '';
  for (const [key, preset] of Object.entries(ROL_PRESETS)) {
    const match = Object.keys(preset.macht).every(k => !!m[k] === !!preset.macht[k]);
    if (match) return key;
  }
  return 'custom';
}

export default function VerkopersPanel() {
  const { user: authUser } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const canCommissies = isAdmin || !!(authUser?.machtigingen || {}).commissies_beheren;
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editId, setEditId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showPin, setShowPin] = useState(false);
  const [showPwd, setShowPwd] = useState(false);
  const [stuurWelkomstmail, setStuurWelkomstmail] = useState(true);
  const [nieuweCredentials, setNieuweCredentials] = useState(null);
  const [resetTarget, setResetTarget] = useState(null); // { v, nieuw_pw, stuurMail }
  const [resetBusy, setResetBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null); // { v, pw, mailOk, mailError }
  const [open, setOpen] = useState(false);
  const [matrixOpen, setMatrixOpen] = useState(true);
  // Data voor commissie-regels dropdowns: artikels + categorieën + prijsgroepen
  const [artikels, setArtikels] = useState([]);
  const [categorieen, setCategorieen] = useState([]);
  const [prijsgroepen, setPrijsgroepen] = useState([]);

  const load = () => {
    API.get('/verkopers').then(r => setItems(r.data || [])).catch(() => setItems([]));
  };
  useEffect(() => {
    load();
    API.get('/artikels').then(r => setArtikels(r.data || [])).catch(() => {});
    API.get('/artikel-params').then(r => {
      setCategorieen(r.data?.categorieen || []);
      setPrijsgroepen(r.data?.groepen || []);
    }).catch(() => {});
  }, []);

  const reset = () => { setForm(emptyForm); setEditId(null); setOpen(false); };

  const save = async () => {
    if (!form.naam.trim()) { alert('Naam is verplicht.'); return; }
    // Bij AANMAKEN: e-mail + wachtwoord verplicht voor login
    if (!editId) {
      if (!form.email.trim()) { alert('E-mail is verplicht — verkoper logt hiermee in.'); return; }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) { alert('Ongeldig e-mailadres.'); return; }
      if (!form.password || form.password.length < 6) { alert('Wachtwoord is verplicht (min. 6 tekens) — gebruik "Genereer" voor een veilig wachtwoord.'); return; }
    }
    if (form.pincode && (!/^\d{4,6}$/.test(form.pincode))) { alert('Pincode moet 4 tot 6 cijfers bevatten.'); return; }
    setBusy(true);
    try {
      const payload = { ...form };
      // Bij bewerken: lege pincode/wachtwoord = niet wijzigen
      if (editId && !payload.pincode) delete payload.pincode;
      if (editId && !payload.password) delete payload.password;
      payload.commissie_regels = (payload.commissie_regels || []).filter(r =>
        r.scope && r.doel && ((Number(r.pct) || 0) > 0 || (Number(r.vast_per_stuk) || 0) > 0)
      );
      if (!canCommissies) {
        delete payload.commissie_config;
        delete payload.commissie_regels;
      }
      if (editId) await API.put(`/verkopers/${editId}`, payload);
      else {
        // Verzend welkomstmail-flag mee bij aanmaken
        if (stuurWelkomstmail) payload.stuur_welkomstmail = true;
        const res = await API.post('/verkopers', payload);
        const mailStatus = res.data?.welkomstmail;
        // One-time toon van credentials aan admin
        setNieuweCredentials({
          naam: form.naam, email: form.email, password: form.password,
          welkomstmail: mailStatus,
        });
      }
      reset();
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Opslaan mislukt');
    } finally { setBusy(false); }
  };

  // Pure helper: genereer veilig 10-tekens wachtwoord (alfanumeriek + speciaal)
  const _genereerVeiligWachtwoord = () => {
    const lower = 'abcdefghjkmnpqrstuvwxyz';
    const upper = 'ABCDEFGHJKMNPQRSTUVWXYZ';
    const digits = '23456789';
    const special = '!@#$&*';
    const all = lower + upper + digits + special;
    const pick = (s) => s[Math.floor(Math.random() * s.length)];
    let pwd = pick(lower) + pick(upper) + pick(digits) + pick(special);
    for (let i = 4; i < 10; i++) pwd += pick(all);
    return pwd.split('').sort(() => Math.random() - 0.5).join('');
  };

  // Genereer veilig wachtwoord voor de hoofdform
  const genereerWachtwoord = () => {
    const pwd = _genereerVeiligWachtwoord();
    setForm(p => ({ ...p, password: pwd }));
    setShowPwd(true); // toon meteen zodat admin het kan kopiëren
  };

  // 🔓 Lockout opheffen voor een verkoper (na 5 foute pogingen)
  const lockoutOpheffen = async (v) => {
    if (!v.email) { alert('Verkoper heeft geen email — geen lockout mogelijk'); return; }
    if (!window.confirm(`Lockout opheffen voor ${v.naam} (${v.email})?\n\nDe verkoper kan direct opnieuw inloggen, zonder 15 minuten wachten.`)) return;
    try {
      const r = await API.post('/admin/clear-lockouts', { email: v.email });
      alert(`✅ ${r.data?.verwijderd ?? 0} lockout-record(s) gewist voor ${v.email}.\n${v.naam} kan nu direct opnieuw inloggen.`);
    } catch (e) {
      alert(e.response?.data?.detail || 'Lockout opheffen mislukt');
    }
  };

  const openResetModal = (v) => {
    setResetTarget({
      v,
      nieuw_pw: _genereerVeiligWachtwoord(),
      stuurMail: !!v.email,
    });
    setResetResult(null);
  };

  // 🔑 Voer de wachtwoord-reset uit (via bestaande `/verkopers/{id}/welkomstmail`-endpoint)
  const voerResetUit = async () => {
    if (!resetTarget) return;
    const { v, nieuw_pw, stuurMail } = resetTarget;
    if (!nieuw_pw || nieuw_pw.length < 6) { alert('Wachtwoord moet minstens 6 tekens bevatten'); return; }
    setResetBusy(true);
    try {
      let mailOk = false;
      let mailError = '';
      if (stuurMail && v.email) {
        // Endpoint verstuurt mail én slaat het nieuwe wachtwoord op
        try {
          const r = await API.post(`/verkopers/${v.id}/welkomstmail`, { nieuw_wachtwoord: nieuw_pw });
          mailOk = !!(r.data?.ok);
          if (!mailOk) mailError = r.data?.error || 'Onbekende fout bij verzenden';
        } catch (e) {
          mailError = e.response?.data?.detail || e.message || 'E-mail verzenden mislukt';
        }
      } else {
        // Geen mail → enkel wachtwoord wijzigen via PUT /verkopers/{id}
        await API.put(`/verkopers/${v.id}`, { password: nieuw_pw });
      }
      setResetResult({ v, pw: nieuw_pw, mailOk, mailError, mailVerzonden: stuurMail && !!v.email });
      setResetTarget(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Wachtwoord-reset mislukt');
    } finally {
      setResetBusy(false);
    }
  };

  const startEdit = async (v) => {
    // BUG-FIX: laad ALLE 22 machtigingen-keys vanuit MACHT_LABELS, niet enkel 7.
    // Zonder deze fix zou de form de ontbrekende keys submitten als `undefined`,
    // waarna de backend ze normaliseert naar `false` → machtigingen "niet wijzigbaar".
    const macht = _alleMachtigingen(v.machtigingen || {});
    // Probeer huidige pincode op te halen (admin-only). Bij 403 → blijft leeg.
    let huidigPin = '';
    let huidigPw = '';
    let pwIsPlainBeschikbaar = false;
    try {
      const pinResp = await API.get(`/verkopers/${v.id}/pincode`);
      huidigPin = pinResp.data?.pincode || '';
    } catch { /* niet-admin → veld blijft leeg, met "leeg = niet wijzigen"-hint */ }
    // Probeer huidig wachtwoord plain-text op te halen (admin-only, enkel als plain beschikbaar in DB).
    // Oudere verkopers hebben enkel een hash en geen plain-text → `heeft_plain=false` → veld blijft leeg.
    try {
      const pwResp = await API.get(`/verkopers/${v.id}/wachtwoord`);
      huidigPw = pwResp.data?.wachtwoord || '';
      pwIsPlainBeschikbaar = !!pwResp.data?.heeft_plain;
    } catch { /* niet-admin of geen plain → veld blijft leeg */ }
    setForm({
      naam: v.naam || '',
      email: v.email || '',
      tel: v.tel || '',
      pincode: huidigPin, // Voor admins: pre-fill met huidige pincode (zodat ze hem zien & kunnen aanpassen)
      password: huidigPw, // Voor admins: pre-fill met huidig plain-wachtwoord als beschikbaar (anders leeg)
      heeft_wachtwoord: !!v.heeft_wachtwoord, // banner: "Wachtwoord is reeds ingesteld" vs "geen wachtwoord"
      pw_is_plain_beschikbaar: pwIsPlainBeschikbaar, // hint of het veld pre-filled werd
      actief: v.actief !== false,
      rol: v.rol || detecteerRolUitMachtigingen(macht),
      machtigingen: macht,
      commissie_config: {
        type: v.commissie_config?.type || 'uit',
        basis: v.commissie_config?.basis || 'excl_btw',
        vast_pct: Number(v.commissie_config?.vast_pct || 0),
        getrapt: Array.isArray(v.commissie_config?.getrapt) ? v.commissie_config.getrapt : [],
      },
      commissie_regels: Array.isArray(v.commissie_regels) ? v.commissie_regels.map(r => ({ ...r })) : [],
    });
    setEditId(v.id);
    setOpen(true);
    // Scroll direct naar de commissie-regels sectie (dat is waar gebruikers meestal naartoe willen bij bewerken).
    // Fallback: naar het form zelf als de sectie nog niet in DOM is.
    setTimeout(() => {
      const target = document.querySelector('[data-testid="commissie-regels-sectie"]') || document.querySelector('[data-testid="verkoper-form"]');
      if (target) {
        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        // Korte visuele highlight zodat de gebruiker ziet waar hij is beland
        target.style.transition = 'box-shadow 300ms ease-out';
        target.style.boxShadow = '0 0 0 3px rgba(249, 115, 22, 0.4)';
        setTimeout(() => { target.style.boxShadow = ''; }, 1200);
      }
    }, 80);
  };

  const remove = async (v) => {
    if (!window.confirm(`Verkoper "${v.naam}" verwijderen?`)) return;
    try { await API.delete(`/verkopers/${v.id}`); load(); }
    catch (e) { alert(e.response?.data?.detail || 'Verwijderen mislukt'); }
  };

  const toggleMacht = (key) => setForm(p => {
    const next = { ...p.machtigingen, [key]: !p.machtigingen[key] };
    // Detecteer of de nieuwe combinatie exact overeenkomt met een preset, anders 'custom'
    return { ...p, machtigingen: next, rol: detecteerRolUitMachtigingen(next) };
  });

  return (
    <div data-testid="verkopers-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <UserCircle size={22} className="text-[#002FA7]" weight="duotone" />
          <h2 className="text-lg font-bold text-gray-900">Verkopers</h2>
          <span className="text-xs text-gray-500 font-mono">{items.length}</span>
        </div>
        {!open && (
          <div className="flex items-center gap-2">
            <button
              className="btn btn-ghost btn-sm inline-flex items-center gap-1 text-purple-700 border border-purple-300 hover:bg-purple-50 font-semibold"
              onClick={async () => {
                const email = window.prompt('Email van verkoper die niet kan inloggen?');
                if (!email) return;
                const password = window.prompt(`Wachtwoord van ${email}?\n(Wordt enkel gebruikt voor 1x test — niet opgeslagen)`);
                if (!password) return;
                try {
                  const r = await API.post('/verkopers/deep-debug-login', { email, password });
                  const d = r.data || {};
                  const summary = `🔬 DEEP DEBUG — ${email}\n\nFINAL: ${d.final}\n\n` + d.trace.map((s, i) => `${i + 1}. ${s.stap}: ${JSON.stringify({ ...s, stap: undefined }, null, 0).replace(/[{}"]/g, '').replace(/,/g, ', ')}`).join('\n');
                  alert(summary);
                  console.log('Deep debug trace:', d);
                } catch (e) {
                  alert('Mislukt: ' + (e.response?.data?.detail || e.message));
                }
              }}
              title="Voer DE EXACTE login-flow uit + toon stap-voor-stap trace"
              data-testid="verkopers-deep-debug-login"
            >
              🔬 Deep Debug
            </button>
            <button
              className="btn btn-ghost btn-sm inline-flex items-center gap-1 text-red-700 border border-red-300 hover:bg-red-50 font-semibold"
              onClick={async () => {
                if (!window.confirm('Detecteer & verwijder DUPLICAAT-verkopers (zelfde email 2× of meer)?\n\nBehoudt het record met password_hash en wist de rest. Lost login-bugs op waar de query een verkeerd record vindt.')) return;
                try {
                  const r = await API.post('/verkopers/dedup-by-email');
                  const d = r.data || {};
                  alert(
                    `🧹 Dedup klaar\n\n` +
                    `Duplicate groepen: ${d.duplicate_groups}\n` +
                    `Records gewist: ${d.gewist_totaal}\n\n` +
                    (d.gewist_totaal ? `Details:\n${d.details.map(x => `  ${x.email}: behoud ${x.behouden_id}, wis ${x.gewist_ids.join(',')}`).join('\n')}` : 'Geen duplicaten gevonden.')
                  );
                  load();
                } catch (e) {
                  alert('Mislukt: ' + (e.response?.data?.detail || e.message));
                }
              }}
              title="Verwijder duplicate verkopers met identieke email (behoudt record met hash)"
              data-testid="verkopers-dedup-by-email"
            >
              🧹 Dedup
            </button>
            <button
              className="btn btn-ghost btn-sm inline-flex items-center gap-1 text-orange-700 border border-orange-300 hover:bg-orange-50 font-semibold"
              onClick={async () => {
                if (!window.confirm('Resync de password-hash voor ALLE verkopers waar plain & hash niet matchen?\n\nDit lost login-bugs op zonder iets aan plain-wachtwoorden te wijzigen.')) return;
                try {
                  const r = await API.post('/verkopers/resync-all-hashes');
                  const d = r.data || {};
                  alert(
                    `✅ Klaar\n\n` +
                    `Hersyncd: ${d.geresynct}\n` +
                    `Reeds OK: ${d.al_ok}\n` +
                    `Zonder plain: ${d.geen_plain}\n\n` +
                    (d.geresynct ? `Verkopers ${d.details.map(v => v.naam).join(', ')} kunnen nu opnieuw inloggen.` : '')
                  );
                } catch (e) {
                  alert('Mislukt: ' + (e.response?.data?.detail || e.message));
                }
              }}
              title="Bulk-fix: regenereert password_hash voor verkopers waar plain & hash out-of-sync zijn"
              data-testid="verkopers-resync-all-hashes"
            >
              🔧 Resync hashes
            </button>
            <button className="btn btn-primary inline-flex items-center gap-1.5 font-bold" onClick={() => { reset(); setOpen(true); }} data-testid="verkoper-nieuw">
              <Plus size={14} /> Nieuwe verkoper
            </button>
          </div>
        )}
      </div>

      {/* Formulier */}
      {open && (
        <div className="border-2 border-[#002FA7]/30 bg-[#F0F4FF]/50 p-4 mb-5" data-testid="verkoper-form">
          <h3 className="text-sm font-bold text-gray-900 mb-3">{editId ? 'Verkoper bewerken' : 'Nieuwe verkoper'}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Naam *</label>
              <input className="field-input" value={form.naam} onChange={e => setForm({ ...form, naam: e.target.value })} placeholder="bv. Jan Peeters" data-testid="verkoper-naam" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">
                E-mail {!editId && <span className="text-red-500">*</span>}
              </label>
              <input type="email" className="field-input" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} placeholder="jan@bedrijf.be" autoComplete="email" data-testid="verkoper-email" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Telefoon</label>
              <input className="field-input" value={form.tel} onChange={e => setForm({ ...form, tel: e.target.value })} placeholder="+32 472 ..." data-testid="verkoper-tel" />
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 inline-flex items-center gap-1 mb-1">
                <Key size={11} /> Pincode (4-6 cijfers) {editId && <span className="font-normal text-gray-500 lowercase italic ml-1">leeg = niet wijzigen</span>}
              </label>
              <div className="relative">
                <input
                  type={showPin ? 'text' : 'password'}
                  inputMode="numeric"
                  maxLength={6}
                  className="field-input font-mono pr-10"
                  value={form.pincode}
                  onChange={e => setForm({ ...form, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })}
                  placeholder={editId ? '••••' : 'bv. 4321'}
                  data-testid="verkoper-pincode"
                />
                <button
                  type="button"
                  className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-gray-500 hover:text-[#002FA7] hover:bg-gray-50"
                  onClick={() => setShowPin(s => !s)}
                  title={showPin ? 'Pincode verbergen' : 'Pincode tonen'}
                  data-testid="verkoper-pincode-toggle"
                >
                  {showPin ? '🙈' : '👁️'}
                </button>
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 inline-flex items-center gap-1 mb-1">
                🔐 Wachtwoord (voor email-login) {!editId && <span className="text-red-500">*</span>}
                <span className="font-normal text-gray-500 lowercase italic ml-1">{editId ? 'leeg = niet wijzigen' : 'min. 6 tekens'}</span>
              </label>
              {editId && form.heeft_wachtwoord && form.pw_is_plain_beschikbaar && (
                <div className="text-[11px] bg-green-50 border border-green-200 text-green-800 px-2 py-1.5 mb-1.5 flex items-center gap-1.5" data-testid="verkoper-pw-status-banner">
                  <span className="font-bold">✓ Huidig wachtwoord zichtbaar hieronder.</span>
                  <span>Klik op het 👁️-icoon om het te tonen, of pas het aan om te wijzigen.</span>
                </div>
              )}
              {editId && form.heeft_wachtwoord && !form.pw_is_plain_beschikbaar && (
                <div className="text-[11px] bg-amber-50 border border-amber-200 text-amber-800 px-2 py-1.5 mb-1.5 flex items-center gap-1.5" data-testid="verkoper-pw-status-banner">
                  <span className="font-bold">🔒 Oud wachtwoord — niet zichtbaar.</span>
                  <span>Dit account werd aangemaakt vóór de "altijd zichtbaar"-feature. Typ een nieuw wachtwoord en sla op om het voortaan te kunnen zien.</span>
                </div>
              )}
              {editId && !form.heeft_wachtwoord && (
                <div className="text-[11px] bg-red-50 border border-red-200 text-red-800 px-2 py-1.5 mb-1.5" data-testid="verkoper-pw-status-banner">
                  ⚠️ <strong>Geen wachtwoord ingesteld</strong> — deze verkoper kan NIET inloggen via /login tot je hier een wachtwoord typt en opslaat.
                </div>
              )}
              <div className="relative flex gap-1">
                <div className="relative flex-1">
                  <input
                    type={showPwd ? 'text' : 'password'}
                    className="field-input pr-10 w-full"
                    value={form.password}
                    onChange={e => setForm({ ...form, password: e.target.value })}
                    placeholder={editId ? '••••••••' : 'minstens 6 tekens'}
                    autoComplete="new-password"
                    data-testid="verkoper-password"
                  />
                  <button
                    type="button"
                    className="absolute right-1 top-1/2 -translate-y-1/2 px-2 py-1 text-xs text-gray-500 hover:text-[#002FA7] hover:bg-gray-50"
                    onClick={() => setShowPwd(s => !s)}
                    title={showPwd ? 'Wachtwoord verbergen' : 'Wachtwoord tonen'}
                    data-testid="verkoper-password-toggle"
                  >
                    {showPwd ? '🙈' : '👁️'}
                  </button>
                </div>
                <button
                  type="button"
                  className="px-3 py-1.5 bg-[#002FA7] hover:bg-[#001f7a] text-white text-xs font-bold whitespace-nowrap"
                  onClick={genereerWachtwoord}
                  title="Genereer een veilig wachtwoord van 10 tekens"
                  data-testid="verkoper-password-genereer"
                >
                  ⚡ Genereer
                </button>
                {editId && (
                  <button
                    type="button"
                    className="px-3 py-1.5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold whitespace-nowrap"
                    onClick={async () => {
                      try {
                        // Stuur het huidige wachtwoord-veld mee zodat de simulatie de exact login-flow test
                        const pw = form.password || '';
                        const d = await API.post(`/verkopers/${editId}/diagnose-login`, { password: pw });
                        const r = d.data || {};
                        const sim = r.simulation;
                        const lines = [
                          `📋 Diagnose — ${r.naam}`,
                          `Email in DB: ${r.email}`,
                          `Email lowercase OK: ${r.email_is_lowercase ? 'JA ✅' : 'NEE ⚠ (wordt nu auto-genormaliseerd)'}`,
                          `Actief: ${r.actief ? 'JA ✅' : 'NEE 🚫 (login geblokkeerd)'}`,
                          `Heeft hash: ${r.heeft_hash ? 'JA' : 'NEE'}`,
                          `Heeft plain: ${r.heeft_plain ? `JA (${r.plain_lengte} tekens)` : 'NEE'}`,
                          `Hash matcht plain: ${r.hash_matches_plain ? 'JA ✅' : 'NEE ⚠️'}`,
                        ];
                        if (sim) {
                          lines.push('', '─── Simulatie met het ingevulde wachtwoord ───');
                          lines.push(`Ingevuld wachtwoord lengte: ${sim.test_password_lengte}`);
                          lines.push(`Verkoper gevonden via email: ${sim.verkoper_via_email_gevonden ? '✅' : '❌'}`);
                          if (sim.duplicaten_aantal > 1) {
                            lines.push(`⚠️ DUPLICATEN: ${sim.duplicaten_aantal} verkoper-records met deze email!`);
                            lines.push(`   IDs: ${sim.duplicaat_ids.join(', ')}`);
                            lines.push(`   Diagnose-target = login-target: ${sim.diagnose_target_is_login_target ? '✅' : '❌ (login pakt ander record!)'}`);
                          }
                          lines.push(`Actief check: ${sim.actief_check_pass ? '✅' : '❌'}`);
                          lines.push(`Bcrypt verify: ${sim.bcrypt_verify_pass ? '✅' : '❌'}`);
                          lines.push(`Plain match: ${sim.plain_text_match_pass ? '✅' : '❌'}`);
                          lines.push(`Self-heal triggert: ${sim.self_heal_zou_triggeren ? 'JA (hash wordt gefixt)' : 'nee'}`);
                          if (sim.tenant_actief === false) {
                            lines.push(`🚫 PARENT-TENANT GEBLOKKEERD: trial verlopen of suspended → login wordt 403`);
                          } else if (sim.tenant_actief === true) {
                            lines.push(`Tenant actief: ✅`);
                          }
                          lines.push(`🎯 FINAL: ${sim.final_login_zou_slagen ? 'LOGIN ZOU SLAGEN ✅' : 'LOGIN FAALT ❌'}`);
                        }
                        lines.push('', `→ ${r.diagnose}`);
                        const allText = lines.join('\n');
                        if (!r.hash_matches_plain && r.heeft_plain && r.heeft_hash) {
                          if (window.confirm(allText + '\n\nWil je de hash NU resyncen? (Aanbevolen)')) {
                            await API.post(`/verkopers/${editId}/resync-hash`);
                            alert('✅ Hash hersynchroniseerd. Probeer nu opnieuw in te loggen op /login.');
                          }
                        } else {
                          alert(allText);
                        }
                      } catch (e) {
                        alert('Diagnose mislukt: ' + (e.response?.data?.detail || e.message));
                      }
                    }}
                    title="Volledige login-simulatie met het ingevulde wachtwoord + automatische hash-resync"
                    data-testid="verkoper-diagnose-login"
                  >
                    🔍 Diagnose
                  </button>
                )}
              </div>
              <div className="text-[10px] text-gray-500 mt-0.5">
                {form.naam || 'De verkoper'} logt in op <strong>/login</strong> met dit e-mailadres en wachtwoord — krijgt automatisch enkel de aangevinkte machtigingen.
              </div>
              {!editId && (
                <label className="mt-2 flex items-center gap-2 text-xs cursor-pointer p-2 border border-blue-200 bg-blue-50/50 hover:bg-blue-50 transition-colors">
                  <input
                    type="checkbox"
                    className="w-4 h-4"
                    checked={stuurWelkomstmail}
                    onChange={e => setStuurWelkomstmail(e.target.checked)}
                    data-testid="verkoper-stuur-welkomstmail"
                  />
                  <span>📧 <strong>Verstuur automatisch een welkomstmail</strong> met inloggegevens naar {form.email || 'het e-mailadres'} (vereist SMTP-configuratie)</span>
                </label>
              )}
            </div>
          </div>

          {/* Machtigingen — Apple-stijl tabel met iOS-toggles + rollen-presets */}
          <div className="mb-3" data-testid="machtigingen-apple-section">
            <div className="flex items-center justify-between mb-3">
              <div className="inline-flex items-center gap-2">
                <span className="text-sm font-semibold text-gray-800">🔒 Machtigingen</span>
                <span className="text-[10px] text-gray-400 font-normal">— tik een toggle om aan/uit te zetten</span>
              </div>
              {/* Preset-pills: klik om een complete set rechten in één keer toe te passen */}
              <div className="inline-flex items-center gap-1.5">
                <span className="text-[10px] uppercase tracking-wider text-gray-500 font-bold">Snelkeuze:</span>
                {Object.entries(ROL_PRESETS).map(([key, rol]) => {
                  const actief = form.rol === key;
                  return (
                    <button
                      key={key}
                      type="button"
                      onClick={() => setForm(p => ({ ...p, machtigingen: { ...rol.macht }, rol: key }))}
                      className="text-[10px] font-bold px-2 py-0.5 transition-all hover:opacity-80"
                      style={{
                        borderRadius: 999,
                        border: `1.5px solid ${rol.kleur}`,
                        color: actief ? '#fff' : rol.kleur,
                        background: actief ? rol.kleur : `${rol.kleur}0D`,
                      }}
                      data-testid={`preset-rol-${key}`}
                      title={`Stel machtigingen in als ${rol.label}`}
                    >
                      {rol.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Lijst met iOS-stijl rijen */}
            <div
              className="overflow-hidden"
              style={{
                background: '#F5F5F7',
                borderRadius: 16,
                border: '1px solid rgba(0,0,0,0.06)',
              }}
            >
              {MACHT_LABELS.map((m, idx) => {
                const on = !!form.machtigingen[m.key];
                return (
                  <div
                    key={m.key}
                    className="flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors"
                    onClick={() => toggleMacht(m.key)}
                    style={{
                      background: on ? 'rgba(255,107,13,0.05)' : '#FFFFFF',
                      borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.06)',
                    }}
                    data-testid={`verkoper-macht-row-${m.key}`}
                  >
                    <div
                      className="flex items-center justify-center shrink-0"
                      style={{
                        width: 36, height: 36, borderRadius: 10,
                        background: on ? '#FF6B0D' : '#E5E7EB',
                        fontSize: 18,
                        transition: 'background-color 0.2s',
                      }}
                    >
                      <span style={{ filter: on ? 'brightness(0) invert(1)' : 'none' }}>{m.icon}</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-semibold text-gray-900 leading-tight">{m.label}</div>
                      <div className="text-[11px] text-gray-500 leading-tight mt-0.5">{m.hint}</div>
                    </div>
                    <IOSSwitch
                      checked={on}
                      onChange={() => toggleMacht(m.key)}
                      testid={`verkoper-macht-${m.key}`}
                    />
                  </div>
                );
              })}
            </div>

          </div>

          <div className="flex items-center gap-3 mb-3">
            <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer">
              <input type="checkbox" checked={form.actief} onChange={e => setForm({ ...form, actief: e.target.checked })} data-testid="verkoper-actief" />
              Actief (verkoper kan inloggen en bestellingen plaatsen)
            </label>
          </div>

          {/* Commissie-configuratie — alleen admin/commissies_beheren */}
          {canCommissies && (
          <div className="mb-4 border-t border-gray-200 pt-3">
            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-600 mb-2 inline-flex items-center gap-1">
              💶 Commissie-regeling
            </div>
            <div className="grid grid-cols-2 gap-2 mb-2">
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Type</label>
                <select className="field-input" value={form.commissie_config.type} onChange={e => setForm(p => ({ ...p, commissie_config: { ...p.commissie_config, type: e.target.value } }))} data-testid="commissie-type">
                  <option value="uit">Uit (geen commissie)</option>
                  <option value="vast">Vast %</option>
                  <option value="getrapt">Getrapt (tiers)</option>
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Basis</label>
                <select className="field-input" value={form.commissie_config.basis} onChange={e => setForm(p => ({ ...p, commissie_config: { ...p.commissie_config, basis: e.target.value } }))} data-testid="commissie-basis" disabled={form.commissie_config.type === 'uit'}>
                  <option value="excl_btw">Omzet excl. BTW</option>
                  <option value="incl_btw">Omzet incl. BTW</option>
                </select>
              </div>
            </div>
            {form.commissie_config.type === 'vast' && (
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Percentage</label>
                <div className="relative">
                  <input type="number" step="0.1" min="0" max="100" className="field-input pr-7" value={form.commissie_config.vast_pct} onChange={e => setForm(p => ({ ...p, commissie_config: { ...p.commissie_config, vast_pct: e.target.value } }))} data-testid="commissie-vast-pct" />
                  <span className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-bold">%</span>
                </div>
                <div className="text-[10px] text-gray-500 mt-1 italic">bv. 5% van de maandelijkse omzet op zijn bestelbonnen</div>
              </div>
            )}
            {form.commissie_config.type === 'getrapt' && (
              <div>
                <label className="text-[10px] uppercase font-bold text-gray-500 block mb-1">Tiers (laat tot-bedrag leeg bij de laatste rij = "en alles daarboven")</label>
                <div className="space-y-1.5">
                  {(form.commissie_config.getrapt || []).map((t, i) => (
                    <div key={i} className="flex items-center gap-2" data-testid={`commissie-tier-${i}`}>
                      <span className="text-[10px] text-gray-500 w-10">tot €</span>
                      <input type="number" step="1" min="0" className="field-input font-mono" style={{ maxWidth: 110 }} value={t.tot_bedrag ?? ''} onChange={e => setForm(p => { const g = [...p.commissie_config.getrapt]; g[i] = { ...g[i], tot_bedrag: e.target.value === '' ? null : Number(e.target.value) }; return { ...p, commissie_config: { ...p.commissie_config, getrapt: g } }; })} placeholder="bv. 10000" />
                      <span className="text-[10px] text-gray-500">→</span>
                      <input type="number" step="0.1" min="0" className="field-input font-mono" style={{ maxWidth: 80 }} value={t.pct ?? 0} onChange={e => setForm(p => { const g = [...p.commissie_config.getrapt]; g[i] = { ...g[i], pct: Number(e.target.value) }; return { ...p, commissie_config: { ...p.commissie_config, getrapt: g } }; })} />
                      <span className="text-[10px] font-bold text-gray-500">%</span>
                      <button type="button" className="p-1 text-red-500 hover:bg-red-50" onClick={() => setForm(p => ({ ...p, commissie_config: { ...p.commissie_config, getrapt: p.commissie_config.getrapt.filter((_, idx) => idx !== i) } }))}><Trash size={14} /></button>
                    </div>
                  ))}
                  <button type="button" className="btn btn-ghost btn-sm text-xs inline-flex items-center gap-1" onClick={() => setForm(p => ({ ...p, commissie_config: { ...p.commissie_config, getrapt: [...(p.commissie_config.getrapt || []), { tot_bedrag: null, pct: 0 }] } }))} data-testid="commissie-tier-toevoegen">
                    <Plus size={12} /> Schijf toevoegen
                  </button>
                </div>
                <div className="text-[10px] text-gray-500 mt-1 italic">
                  Voorbeeld: schijf "tot €10.000 → 3%" + schijf "(leeg) → 5%" = eerste €10k krijgt 3%, alles daarboven 5%.
                </div>
              </div>
            )}
          </div>
          )}

          {/* Fijnmazige commissie-regels — override per artikel / categorie / prijsgroep (alleen admin/commissies_beheren) */}
          {canCommissies && (
          <div className="mb-4 border-t border-gray-200 pt-3" data-testid="commissie-regels-sectie">
            <div className="text-[10px] uppercase font-bold tracking-wider text-gray-600 mb-2 inline-flex items-center gap-1">
              🎯 Specifieke commissies (override op standaard hierboven)
            </div>
            <p className="text-[10px] text-gray-500 mb-2 italic">
              Prioriteit: artikel &gt; categorie &gt; prijsgroep &gt; standaard. Bv. "CW Profiel → €10/st op winst" of "Kassa-prijsgroep → 30%".
            </p>
            <div className="space-y-1.5">
              {(form.commissie_regels || []).map((r, i) => {
                const setR = (patch) => setForm(p => ({ ...p, commissie_regels: p.commissie_regels.map((x, idx) => idx === i ? { ...x, ...patch } : x) }));
                const del = () => setForm(p => ({ ...p, commissie_regels: p.commissie_regels.filter((_, idx) => idx !== i) }));
                return (
                  <div key={i} className="flex flex-wrap items-center gap-1.5 p-2 bg-gray-50 border border-gray-200" data-testid={`commissie-regel-${i}`}>
                    <select className="field-input text-xs" style={{ maxWidth: 110 }} value={r.scope} onChange={e => setR({ scope: e.target.value, doel: '' })}>
                      <option value="artikel">Artikel</option>
                      <option value="categorie">Categorie</option>
                      <option value="prijsgroep">Prijsgroep</option>
                    </select>
                    {r.scope === 'artikel' ? (
                      <select className="field-input text-xs" style={{ maxWidth: 180 }} value={r.doel} onChange={e => setR({ doel: e.target.value })}>
                        <option value="">-- kies artikel --</option>
                        {artikels.map(a => <option key={a.id} value={a.art_code}>{a.art_code} — {(a.omschrijving || '').slice(0, 30)}</option>)}
                      </select>
                    ) : r.scope === 'categorie' ? (
                      <select className="field-input text-xs" style={{ maxWidth: 160 }} value={r.doel} onChange={e => setR({ doel: e.target.value })}>
                        <option value="">-- kies categorie --</option>
                        {categorieen.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                    ) : (
                      <select className="field-input text-xs" style={{ maxWidth: 140 }} value={r.doel} onChange={e => setR({ doel: e.target.value })}>
                        <option value="">-- kies prijsgroep --</option>
                        {prijsgroepen.map(g => {
                          const lbl = (g.label || g.key || '').charAt(0).toUpperCase() + (g.label || g.key || '').slice(1);
                          return <option key={g.key} value={lbl}>{lbl}</option>;
                        })}
                      </select>
                    )}
                    <div className="inline-flex items-center gap-0.5">
                      <input type="number" step="0.1" min="0" max="100" className="field-input text-xs font-mono" style={{ maxWidth: 70 }} value={r.pct || 0} onChange={e => setR({ pct: Number(e.target.value) })} placeholder="%" title="Percentage" />
                      <span className="text-[10px] text-gray-500 font-bold">%</span>
                    </div>
                    <div className="inline-flex items-center gap-0.5">
                      <span className="text-[10px] text-gray-500 font-bold">€</span>
                      <input type="number" step="0.5" min="0" className="field-input text-xs font-mono" style={{ maxWidth: 70 }} value={r.vast_per_stuk || 0} onChange={e => setR({ vast_per_stuk: Number(e.target.value) })} placeholder="€/st" title="Vast bedrag per stuk" />
                      <span className="text-[10px] text-gray-500">/st</span>
                    </div>
                    <select className="field-input text-xs" style={{ maxWidth: 90 }} value={r.basis || 'winst'} onChange={e => setR({ basis: e.target.value })} title="Basis voor percentage">
                      <option value="winst">op winst</option>
                      <option value="verkoop">op omzet</option>
                    </select>
                    <button type="button" className="p-1 text-red-500 hover:bg-red-50 ml-auto" onClick={del} data-testid={`del-comm-regel-${i}`}><Trash size={14} /></button>
                  </div>
                );
              })}
              <button
                type="button"
                className="btn btn-ghost btn-sm text-xs inline-flex items-center gap-1"
                onClick={() => setForm(p => ({ ...p, commissie_regels: [...(p.commissie_regels || []), { scope: 'categorie', doel: '', pct: 0, vast_per_stuk: 0, basis: 'winst' }] }))}
                data-testid="add-commissie-regel"
              >
                <Plus size={12} /> Regel toevoegen
              </button>
            </div>
          </div>
          )}

          <div className="flex gap-2">
            <button className="btn btn-primary font-bold inline-flex items-center gap-1.5" onClick={save} disabled={busy} data-testid="verkoper-opslaan">
              {busy ? 'Bezig...' : editId ? 'Wijzigingen opslaan' : 'Verkoper aanmaken'}
            </button>
            <button className="btn btn-secondary" onClick={reset}>Annuleren</button>
          </div>
        </div>
      )}

      {/* Overzichtsmatrix — iOS-stijl Settings-kaart */}
      {items.length >= 1 && (
        <div className="mb-5" data-testid="machtigingen-matrix-section">
          <button
            type="button"
            onClick={() => setMatrixOpen(v => !v)}
            className="w-full flex items-center justify-between px-4 py-3 transition-all"
            style={{
              background: '#FFFFFF',
              border: '1px solid rgba(0,0,0,0.06)',
              borderRadius: matrixOpen ? '16px 16px 0 0' : 16,
              borderBottom: matrixOpen ? '1px solid rgba(0,0,0,0.06)' : '1px solid rgba(0,0,0,0.06)',
              boxShadow: matrixOpen ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
              cursor: 'pointer',
            }}
            data-testid="matrix-toggle"
          >
            <span className="inline-flex items-center gap-3">
              <span
                className="flex items-center justify-center"
                style={{ width: 36, height: 36, borderRadius: 10, background: '#FF6B0D', fontSize: 16, boxShadow: '0 0 10px rgba(255,107,13,0.35)' }}
              >
                <span style={{ filter: 'brightness(0) invert(1)' }}>📊</span>
              </span>
              <span className="flex flex-col items-start">
                <span className="text-[14px] font-semibold text-gray-900 leading-tight">Machtigingen-matrix</span>
                <span className="text-[11px] text-gray-500 leading-tight mt-0.5">Overzicht rechten per verkoper · {items.filter(v => v.actief !== false).length} actief</span>
              </span>
            </span>
            <span
              className="text-gray-400 transition-transform"
              style={{ fontSize: 18, transform: matrixOpen ? 'rotate(90deg)' : 'rotate(0deg)', transition: 'transform 0.2s cubic-bezier(0.4, 0, 0.2, 1)' }}
            >
              ›
            </span>
          </button>
          {matrixOpen && (
            <div
              className="overflow-x-auto"
              style={{
                background: '#F5F5F7',
                border: '1px solid rgba(0,0,0,0.06)',
                borderTop: 'none',
                borderRadius: '0 0 16px 16px',
              }}
            >
              {items.filter(v => v.actief !== false).length === 0 ? (
                <div className="text-center py-8 text-[13px] text-gray-500 italic">Geen actieve verkopers — activeer eerst een verkoper om de matrix te zien.</div>
              ) : (
                <div style={{ padding: 8 }}>
                  <table className="w-full" style={{ borderCollapse: 'separate', borderSpacing: 0, background: '#FFFFFF', borderRadius: 12, overflow: 'hidden' }}>
                    <thead>
                      <tr>
                        <th
                          className="text-left px-4 py-3 font-semibold text-gray-500 sticky left-0 text-[11px] uppercase tracking-wider"
                          style={{ background: '#FFFFFF', borderBottom: '1px solid rgba(0,0,0,0.06)', minWidth: 220 }}
                        >
                          Machtiging
                        </th>
                        {items.filter(v => v.actief !== false).map(v => {
                          const rolKey = v.rol || detecteerRolUitMachtigingen(v.machtigingen);
                          const rol = ROL_PRESETS[rolKey];
                          const initials = (v.naam || '?').split(' ').map(s => s[0]).filter(Boolean).slice(0, 2).join('').toUpperCase();
                          return (
                            <th
                              key={v.id}
                              className="px-3 py-3 whitespace-nowrap cursor-pointer hover:bg-[#F0F4FF]/60 transition-colors group/header"
                              style={{ borderBottom: '1px solid rgba(0,0,0,0.06)', minWidth: 120 }}
                              onClick={() => startEdit(v)}
                              title={`Bewerk ${v.naam} (commissies, pincode, rol...)`}
                              data-testid={`matrix-verkoper-open-${v.id}`}
                            >
                              <div className="flex flex-col items-center gap-1.5">
                                <div
                                  className="flex items-center justify-center text-white font-bold text-[11px] relative"
                                  style={{
                                    width: 32, height: 32, borderRadius: '50%',
                                    background: rol ? rol.kleur : '#6B7280',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
                                  }}
                                >
                                  {initials}
                                </div>
                                <span className="text-[12px] font-semibold text-gray-900 leading-tight group-hover/header:text-[#002FA7] group-hover/header:underline">{v.naam}</span>
                                {rol ? (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide" style={{ background: `${rol.kleur}15`, color: rol.kleur }}>
                                    {rol.label}
                                  </span>
                                ) : (
                                  <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide" style={{ background: '#F3F4F6', color: '#6B7280' }}>
                                    Custom
                                  </span>
                                )}
                                {/* Expliciete "Bewerken" knop — helpt gebruikers die de rij-klik niet ontdekken */}
                                <button
                                  type="button"
                                  className="text-[10px] font-bold text-[#002FA7] hover:text-white hover:bg-[#002FA7] border border-[#002FA7] px-2 py-0.5 rounded-full transition-colors mt-0.5"
                                  onClick={(e) => { e.stopPropagation(); startEdit(v); }}
                                  data-testid={`matrix-verkoper-edit-${v.id}`}
                                >
                                  ✏️ Bewerk
                                </button>
                              </div>
                            </th>
                          );
                        })}
                      </tr>
                    </thead>
                    <tbody>
                      {MACHT_LABELS.map((m, idx) => (
                        <tr
                          key={m.key}
                          className="group"
                          style={{ borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.04)' }}
                        >
                          <td
                            className="px-4 py-2.5 sticky left-0 bg-white group-hover:bg-[#F9FAFB] transition-colors"
                            style={{ borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.04)' }}
                          >
                            <div className="flex items-center gap-2.5">
                              <div
                                className="flex items-center justify-center shrink-0"
                                style={{ width: 28, height: 28, borderRadius: 8, background: '#F5F5F7', fontSize: 14 }}
                              >
                                {m.icon}
                              </div>
                              <div className="flex flex-col min-w-0">
                                <span className="text-[13px] font-semibold text-gray-900 leading-tight">{m.label}</span>
                                <span className="text-[10px] text-gray-500 leading-tight mt-0.5 truncate" style={{ maxWidth: 280 }}>{m.hint}</span>
                              </div>
                            </div>
                          </td>
                          {items.filter(v => v.actief !== false).map(v => {
                            const has = !!v.machtigingen?.[m.key];
                            return (
                              <td
                                key={v.id}
                                className="text-center px-3 py-2.5 group-hover:bg-[#F9FAFB] transition-colors"
                                style={{ borderTop: idx === 0 ? 'none' : '1px solid rgba(0,0,0,0.04)' }}
                              >
                                {has ? (
                                  <span
                                    className="inline-flex items-center justify-center"
                                    style={{
                                      width: 24, height: 24, borderRadius: '50%',
                                      background: '#34C759',
                                      color: '#fff',
                                      fontSize: 14, fontWeight: 800,
                                      boxShadow: '0 1px 2px rgba(52,199,89,0.3)',
                                    }}
                                    title="Toegestaan"
                                  >
                                    ✓
                                  </span>
                                ) : (
                                  <span
                                    className="inline-block"
                                    style={{
                                      width: 24, height: 3, borderRadius: 2,
                                      background: '#D1D5DB',
                                    }}
                                    title="Niet toegestaan"
                                  />
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  <div className="flex items-center gap-4 px-3 py-2 text-[10px] text-gray-500">
                    <span className="inline-flex items-center gap-1.5">
                      <span style={{ display: 'inline-block', width: 14, height: 14, borderRadius: '50%', background: '#34C759' }}></span> Toegestaan
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <span style={{ display: 'inline-block', width: 14, height: 3, borderRadius: 2, background: '#D1D5DB' }}></span> Niet toegestaan
                    </span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Lijst */}
      {items.length === 0 ? (
        <div className="text-center py-10 text-sm text-gray-500 border border-dashed border-gray-300">
          Nog geen verkopers aangemaakt. Klik op <strong>Nieuwe verkoper</strong> om te beginnen.
        </div>
      ) : (
        <div className="border border-gray-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-[10px] uppercase tracking-wider text-gray-600 font-bold">
              <tr>
                <th className="text-left px-3 py-2">Naam</th>
                <th className="text-left px-3 py-2">Contact</th>
                <th className="text-left px-3 py-2">Wachtwoord</th>
                <th className="text-left px-3 py-2">Pincode</th>
                <th className="text-left px-3 py-2">Machtigingen</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2">Acties</th>
              </tr>
            </thead>
            <tbody>
              {items.map(v => {
                const machts = Object.entries(v.machtigingen || {}).filter(([, on]) => on).map(([k]) => k);
                return (
                  <tr key={v.id} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`verkoper-row-${v.id}`}>
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2">
                        <span className="font-bold">{v.naam}</span>
                        {(() => {
                          const rolKey = v.rol || detecteerRolUitMachtigingen(v.machtigingen);
                          if (!rolKey || rolKey === 'custom') {
                            return <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border" style={{ borderColor: '#9CA3AF', color: '#6B7280', background: '#F3F4F6' }} title="Aangepaste rechten-combinatie">CUSTOM</span>;
                          }
                          const rol = ROL_PRESETS[rolKey];
                          if (!rol) return null;
                          return (
                            <span
                              className="text-[9px] font-bold px-1.5 py-0.5 rounded-full uppercase tracking-wide"
                              style={{ background: rol.kleur, color: '#fff' }}
                              data-testid={`verkoper-rol-badge-${v.id}`}
                              title={`Rol: ${rol.label}`}
                            >
                              {rol.label}
                            </span>
                          );
                        })()}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-xs text-gray-600">
                      {v.email && <div>{v.email}</div>}
                      {v.tel && <div className="font-mono text-[11px]">{v.tel}</div>}
                    </td>
                    <td className="px-3 py-2">
                      {v.heeft_wachtwoord
                        ? <span className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 font-bold" data-testid={`verkoper-pw-status-${v.id}`}>INGESTELD ✓</span>
                        : <span className="text-[10px] px-1.5 py-0.5 bg-red-50 text-red-700 border border-red-200 font-bold" data-testid={`verkoper-pw-status-${v.id}`}>GEEN — kan NIET inloggen</span>}
                    </td>
                    <td className="px-3 py-2">
                      {v.heeft_pincode
                        ? <span className="text-[10px] px-1.5 py-0.5 bg-green-50 text-green-700 border border-green-200 font-bold">INGESTELD</span>
                        : <span className="text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 font-bold">GEEN</span>}
                    </td>
                    <td className="px-3 py-2 text-[11px] text-gray-600">
                      {machts.length === 0 ? <span className="italic text-gray-400">geen</span> : machts.join(' · ')}
                    </td>
                    <td className="px-3 py-2">
                      {v.actief
                        ? <span className="text-[10px] px-1.5 py-0.5 bg-green-100 text-green-800 border border-green-200 font-bold">ACTIEF</span>
                        : <span className="text-[10px] px-1.5 py-0.5 bg-gray-100 text-gray-600 border border-gray-300 font-bold">INACTIEF</span>}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {isAdmin && (
                        <>
                          <button
                            className="p-1.5 hover:bg-blue-100 text-blue-700 mr-1"
                            onClick={() => lockoutOpheffen(v)}
                            title="Lockout opheffen (na te veel foute login-pogingen)"
                            data-testid={`verkoper-clear-lockout-${v.id}`}
                          >
                            <LockOpen size={14} />
                          </button>
                          <button
                            className="p-1.5 hover:bg-amber-100 text-amber-700 mr-1"
                            onClick={() => openResetModal(v)}
                            title="Wachtwoord resetten + welkomstmail versturen"
                            data-testid={`verkoper-reset-pw-${v.id}`}
                          >
                            <Key size={14} />
                          </button>
                        </>
                      )}
                      <button className="p-1.5 hover:bg-gray-200 mr-1" onClick={() => startEdit(v)} title="Bewerken" data-testid={`verkoper-edit-${v.id}`}>
                        <PencilSimple size={14} />
                      </button>
                      <button className="p-1.5 hover:bg-red-100 text-red-600" onClick={() => remove(v)} title="Verwijderen" data-testid={`verkoper-del-${v.id}`}>
                        <Trash size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* 🔑 Reset wachtwoord-modal — admin only */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="verkoper-reset-modal">
          <div className="bg-white max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center justify-center" style={{ width: 36, height: 36, borderRadius: 10, background: '#F59E0B', boxShadow: '0 0 10px rgba(245,158,11,0.35)' }}>
                <Key size={18} weight="bold" color="#fff" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900">Wachtwoord resetten</div>
                <div className="text-xs text-gray-500">Voor {resetTarget.v.naam}</div>
              </div>
            </div>
            <div className="text-xs text-gray-600 mb-3 p-2 bg-amber-50 border border-amber-200 rounded">
              ⚠️ Het huidige wachtwoord wordt onmiddellijk overschreven. De verkoper kan daarna enkel met het nieuwe wachtwoord inloggen.
            </div>
            <label className="block text-[10px] uppercase tracking-wider font-bold text-gray-600 mb-1">Nieuw wachtwoord</label>
            <div className="flex gap-1 mb-3">
              <input
                type="text"
                className="field-input flex-1 font-mono"
                value={resetTarget.nieuw_pw}
                onChange={e => setResetTarget(p => ({ ...p, nieuw_pw: e.target.value }))}
                data-testid="reset-pw-input"
              />
              <button
                type="button"
                className="px-3 py-1.5 bg-[#002FA7] hover:bg-[#001f7a] text-white text-xs font-bold whitespace-nowrap"
                onClick={() => setResetTarget(p => ({ ...p, nieuw_pw: _genereerVeiligWachtwoord() }))}
                data-testid="reset-pw-genereer"
              >
                ⚡ Genereer
              </button>
            </div>
            <label className="flex items-center gap-2 text-xs cursor-pointer p-2 border border-blue-200 bg-blue-50/50 hover:bg-blue-50 mb-4">
              <input
                type="checkbox"
                className="w-4 h-4"
                checked={resetTarget.stuurMail}
                disabled={!resetTarget.v.email}
                onChange={e => setResetTarget(p => ({ ...p, stuurMail: e.target.checked }))}
                data-testid="reset-pw-stuur-mail"
              />
              <span>
                📧 <strong>Verstuur ook een welkomstmail</strong> naar {resetTarget.v.email || <em className="text-gray-500">geen e-mail ingesteld</em>}
              </span>
            </label>
            <div className="flex gap-2">
              <button
                className="btn btn-primary flex-1 font-bold inline-flex items-center justify-center gap-1.5"
                onClick={voerResetUit}
                disabled={resetBusy}
                data-testid="reset-pw-uitvoeren"
              >
                {resetBusy ? 'Bezig...' : '🔑 Wachtwoord resetten'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => setResetTarget(null)}
                disabled={resetBusy}
                data-testid="reset-pw-annuleer"
              >
                Annuleren
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 🔑 Reset-resultaat modal — toont nieuwe credentials + email-status */}
      {resetResult && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="verkoper-reset-result-modal">
          <div className="bg-white max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="text-2xl font-bold text-[#059669] mb-2">✅ Wachtwoord gereset</div>
            <div className="text-sm text-gray-700 mb-4">
              <strong>{resetResult.v.naam}</strong> kan nu inloggen met het nieuwe wachtwoord.
              {resetResult.mailVerzonden && resetResult.mailOk && (
                <span className="block mt-2 px-2 py-1 bg-green-50 border border-green-200 text-green-800 text-xs">
                  ✉️ <strong>Welkomstmail verzonden</strong> naar {resetResult.v.email}.
                </span>
              )}
              {resetResult.mailVerzonden && !resetResult.mailOk && (
                <span className="block mt-2 px-2 py-1 bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs">
                  ⚠️ Welkomstmail niet verzonden: {resetResult.mailError}. Geef het wachtwoord hieronder zelf door.
                </span>
              )}
              {!resetResult.mailVerzonden && (
                <span className="block mt-1">Geef de gegevens zelf door — daarna kan je het wachtwoord niet meer in plain-text opvragen.</span>
              )}
            </div>
            <div className="bg-gray-50 border border-gray-200 p-3 space-y-2 mb-4 font-mono text-xs">
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-500">E-mail</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-gray-200 px-2 py-1.5 select-all">{resetResult.v.email || '—'}</code>
                  {resetResult.v.email && (
                    <button
                      className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-[10px] font-bold"
                      onClick={() => navigator.clipboard?.writeText(resetResult.v.email)}
                      data-testid="reset-result-copy-email"
                    >
                      📋 Kopieer
                    </button>
                  )}
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-500">Nieuw wachtwoord</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-gray-200 px-2 py-1.5 select-all">{resetResult.pw}</code>
                  <button
                    className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-[10px] font-bold"
                    onClick={() => navigator.clipboard?.writeText(resetResult.pw)}
                    data-testid="reset-result-copy-password"
                  >
                    📋 Kopieer
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-semibold"
                onClick={() => {
                  const tekst = `Nieuwe inloggegevens voor ${resetResult.v.naam}:\nE-mail: ${resetResult.v.email || '—'}\nWachtwoord: ${resetResult.pw}\n\nLogin via: ${window.location.origin}/login`;
                  navigator.clipboard?.writeText(tekst);
                }}
                data-testid="reset-result-copy-all"
              >
                📋 Kopieer alles
              </button>
              <button
                className="flex-1 px-3 py-2 bg-[#002FA7] hover:bg-[#001f7a] text-white text-sm font-bold"
                onClick={() => setResetResult(null)}
                data-testid="reset-result-sluit"
              >
                Sluiten ✓
              </button>
            </div>
          </div>
        </div>
      )}

      {/* One-time credentials-modal — toont email + wachtwoord direct na aanmaken zodat admin het kan kopiëren/doorgeven */}
      {nieuweCredentials && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="verkoper-credentials-modal">
          <div className="bg-white max-w-md w-full p-6 shadow-2xl border border-gray-200">
            <div className="text-2xl font-bold text-[#059669] mb-2">✅ Verkoper aangemaakt</div>
            <div className="text-sm text-gray-700 mb-4">
              <strong>{nieuweCredentials.naam}</strong> kan nu inloggen met deze gegevens.
              {nieuweCredentials.welkomstmail?.ok ? (
                <span className="block mt-2 px-2 py-1 bg-green-50 border border-green-200 text-green-800 text-xs">
                  ✉️ <strong>Welkomstmail succesvol verzonden</strong> via {(nieuweCredentials.welkomstmail.provider || '').toUpperCase()} naar {nieuweCredentials.email}.
                </span>
              ) : nieuweCredentials.welkomstmail ? (
                <span className="block mt-2 px-2 py-1 bg-yellow-50 border border-yellow-200 text-yellow-800 text-xs">
                  ⚠️ Welkomstmail niet verzonden: {nieuweCredentials.welkomstmail.error || 'Onbekende fout'}. Geef de gegevens hieronder zelf door.
                </span>
              ) : null}
              {!nieuweCredentials.welkomstmail && (
                <span className="block mt-1">Geef ze door en sluit dit venster — daarna kan je het wachtwoord niet meer in plain-text opvragen.</span>
              )}
            </div>
            <div className="bg-gray-50 border border-gray-200 p-3 space-y-2 mb-4 font-mono text-xs">
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-500">E-mail</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-gray-200 px-2 py-1.5 select-all">{nieuweCredentials.email}</code>
                  <button
                    className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-[10px] font-bold"
                    onClick={() => navigator.clipboard?.writeText(nieuweCredentials.email)}
                    data-testid="creds-copy-email"
                  >
                    📋 Kopieer
                  </button>
                </div>
              </div>
              <div>
                <div className="text-[10px] uppercase font-bold text-gray-500">Wachtwoord</div>
                <div className="flex items-center gap-2">
                  <code className="flex-1 bg-white border border-gray-200 px-2 py-1.5 select-all">{nieuweCredentials.password}</code>
                  <button
                    className="px-2 py-1 bg-gray-200 hover:bg-gray-300 text-gray-700 text-[10px] font-bold"
                    onClick={() => navigator.clipboard?.writeText(nieuweCredentials.password)}
                    data-testid="creds-copy-password"
                  >
                    📋 Kopieer
                  </button>
                </div>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                className="flex-1 px-3 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 text-sm font-semibold"
                onClick={() => {
                  const tekst = `Inloggegevens voor ${nieuweCredentials.naam}:\nE-mail: ${nieuweCredentials.email}\nWachtwoord: ${nieuweCredentials.password}\n\nLogin via: ${window.location.origin}/login`;
                  navigator.clipboard?.writeText(tekst);
                }}
                data-testid="creds-copy-all"
              >
                📋 Kopieer alles
              </button>
              <button
                className="flex-1 px-3 py-2 bg-[#002FA7] hover:bg-[#001f7a] text-white text-sm font-bold"
                onClick={() => setNieuweCredentials(null)}
                data-testid="creds-sluit"
              >
                Sluiten ✓
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
